import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge, Button, Input, Spinner } from 'reactstrap';
import DataTable, { ColumnAlignment } from '@/common/tableData/DataTable';
import ROUTES from '@/routes/routes-enums';
import StandardLayout from '@/common/layouts/StandardLayout';
import CurrencyDisplay from '@/components/common/CurrencyDisplay';
import {
  deleteTournamentRegistrationAdminRest,
  getTournamentRegistrationsAdminRest,
  updateTournamentRegistrationAdminRest,
} from '@/services/tournaments/tournaments-service';

const TournamentRegistrationsPage = () => {
  const { tournamentId } = useParams();
  const [loading, setLoading] = useState(false);
  type RegistrationRow = {
    id: string;
    userId: string;
    isEliminated: boolean;
    eliminationAt: string | null;
    finalPosition: number | null;
    prizeAwarded: number;
    reentriesCount: number;
    user: { id: string; alias: string; countryCode: string | null };
    isInTable: boolean;
    stack: number;
  };

  const [rows, setRows] = useState<RegistrationRow[]>([]);

  const [query, setQuery] = useState('');

  const formatDateTime = (value: string) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(d);
  };

  const load = async () => {
    if (!tournamentId) return;
    setLoading(true);
    try {
      // Be defensive about the response shape to avoid setting rows to undefined in prod
      const res: unknown = await getTournamentRegistrationsAdminRest(tournamentId);
      console.log({ res });

      const extractRows = (value: unknown): RegistrationRow[] => {
        if (Array.isArray(value)) return value as RegistrationRow[];
        if (value && typeof value === 'object') {
          const obj = value as Record<string, unknown>;
          if (Array.isArray(obj.registrations)) return obj.registrations as RegistrationRow[];
          if (Array.isArray(obj.data)) return obj.data as RegistrationRow[];
        }
        return [];
      };

      setRows(extractRows(res));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [tournamentId]);

  const handleToggleEliminated = async (id: string, isEliminated: boolean) => {
    if (!tournamentId) return;
    await updateTournamentRegistrationAdminRest(tournamentId, id, { isEliminated });
    load();
  };

  const handleUpdate = async (id: string, data: { finalPosition?: number | null; prizeAwarded?: number | null }) => {
    if (!tournamentId) return;
    await updateTournamentRegistrationAdminRest(tournamentId, id, data);
    load();
  };

  const handleDelete = async (id: string) => {
    if (!tournamentId) return;
    await deleteTournamentRegistrationAdminRest(tournamentId, id);
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const filteredRows = useMemo(() => {
    if (!query) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) => r.user.alias.toLowerCase().includes(q));
  }, [rows, query]);

  const totalPlayers = rows.length;
  const inTables = rows.filter((r) => r.isInTable).length;
  const eliminated = rows.filter((r) => r.isEliminated).length;
  const totalReentries = rows.reduce((sum, r) => sum + (r.reentriesCount || 0), 0);
  const totalAwarded = rows.reduce((sum, r) => sum + (r.prizeAwarded || 0), 0);

  return (
    <StandardLayout pageTitle="Tournament Registrations" showBackButton onBackClick={() => window.history.back()}>
      <div className="w-100 my-3">
        <DataTable
          title="Registrations"
          data={filteredRows}
          onSearch={(v) => setQuery(String(v))}
          headerExtra={
            <div className="d-flex align-items-center flex-wrap gap-2">
              <Badge color="dark" className="px-2 py-1">
                Total: {totalPlayers}
              </Badge>
              <Badge color="dark" className="px-2 py-1">
                In tables: {inTables}
              </Badge>
              <Badge color="dark" className="px-2 py-1">
                Eliminated: {eliminated}
              </Badge>
              <Badge color="dark" className="px-2 py-1">
                Re-entries: {totalReentries}
              </Badge>
              <Badge color="dark" className="d-flex align-items-center gap-1 px-2 py-1">
                Awarded: <CurrencyDisplay amount={totalAwarded} />
              </Badge>
              <Button
                color="primary"
                size="sm"
                onClick={load}
                disabled={loading}
                className="d-flex align-items-center ms-2 gap-2"
              >
                {loading && <Spinner size="sm" />}
                Refresh
              </Button>
            </div>
          }
          columns={[
            {
              header: 'Alias',
              accessorKey: 'user.alias',
              size: 160,
              cell: ({ row }) => (
                <Link className="text-decoration-underline" to={`${ROUTES.USER_EDIT}/${row.original.userId}`}>
                  {row.original.user.alias}
                </Link>
              ),
              meta: { alignment: ColumnAlignment.LEFT, className: 'text-nowrap' },
            },
            {
              header: 'Eliminated',
              accessorKey: 'isEliminated',
              size: 90,
              cell: ({ row }) => (
                <Input
                  type="checkbox"
                  checked={row.original.isEliminated}
                  onChange={(e) => handleToggleEliminated(row.original.id, e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ width: 18, height: 18 }}
                />
              ),
              meta: { alignment: ColumnAlignment.CENTER, className: 'px-3' },
            },
            {
              header: 'Eliminated At',
              accessorKey: 'eliminationAt',
              size: 210,
              cell: ({ row }) =>
                row.original.eliminationAt ? (
                  <span className="text-muted small" title={new Date(row.original.eliminationAt).toLocaleString()}>
                    {formatDateTime(row.original.eliminationAt)}
                  </span>
                ) : (
                  '-'
                ),
              meta: { alignment: ColumnAlignment.LEFT },
            },
            {
              header: 'Final Position',
              accessorKey: 'finalPosition',
              size: 120,
              cell: ({ row }) => (
                <Input
                  type="number"
                  value={row.original.finalPosition ?? ''}
                  onChange={(e) =>
                    handleUpdate(row.original.id, {
                      finalPosition: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  onClick={(e) => e.stopPropagation()}
                  bsSize="sm"
                  style={{ width: 90 }}
                  placeholder="-"
                  min={1}
                />
              ),
              meta: { alignment: ColumnAlignment.CENTER, className: 'px-2' },
            },
            {
              header: 'Prize Awarded',
              accessorKey: 'prizeAwarded',
              size: 190,
              cell: ({ row }) => (
                <div className="d-flex justify-content-end w-100">
                  <Input
                    type="number"
                    step="0.01"
                    value={row.original.prizeAwarded ?? 0}
                    onChange={(e) => handleUpdate(row.original.id, { prizeAwarded: Number(e.target.value) })}
                    onClick={(e) => e.stopPropagation()}
                    bsSize="sm"
                    style={{ width: 100 }}
                    min={0}
                    className="text-end"
                  />
                </div>
              ),
              meta: { alignment: ColumnAlignment.RIGHT, className: 'pe-3 text-end' },
            },
            {
              header: 'Re-entries',
              accessorKey: 'reentriesCount',
              size: 120,
              cell: ({ row }) => <span className="d-block text-end">{row.original.reentriesCount}</span>,
              meta: { alignment: ColumnAlignment.RIGHT, className: 'px-2 text-nowrap' },
            },
            {
              header: 'Stack',
              accessorKey: 'stack',
              size: 110,
              cell: ({ row }) => (
                <span className="d-block text-end">{row.original.stack?.toLocaleString?.() ?? 0}</span>
              ),
              meta: { alignment: ColumnAlignment.RIGHT, className: 'px-2' },
            },
            {
              header: 'Is In Table',
              accessorKey: 'isInTable',
              size: 120,
              cell: ({ row }) => (
                <Badge color={row.original.isInTable ? 'success' : 'secondary'}>
                  {row.original.isInTable ? 'Yes' : 'No'}
                </Badge>
              ),
              meta: { alignment: ColumnAlignment.CENTER, className: 'px-3' },
            },
            {
              header: 'Actions',
              accessorKey: 'actions',
              size: 120,
              cell: ({ row }) => (
                <Button
                  outline
                  color="danger"
                  size="sm"
                  onClick={(e) => (e.stopPropagation(), handleDelete(row.original.id))}
                >
                  Delete
                </Button>
              ),
              meta: { alignment: ColumnAlignment.CENTER, className: 'px-3' },
            },
          ]}
        />
      </div>
    </StandardLayout>
  );
};

export default TournamentRegistrationsPage;
